import Tagify from "@yaireo/tagify";
import { fetch } from "../framework/api.js";
import { getUITranslation } from "../utils/get_ui_translation";

const tagInput = (element) => {
  const options = {
    enforceWhitelist: true,
    whitelist: JSON.parse(element.dataset.rawWhitelist || "[]"),
    dropdown: {
      maxItems: Infinity,
      enabled: 0,
      closeOnSelect: false,
      classname: "tags-look",
    },
  };

  if (element.dataset.maxTags) {
    options.maxTags = parseInt(element.dataset.maxTags, 10);
  }

  return new Tagify(element, options);
};

const UNKNOWN_CODE_CLASS = "reference-code--unknown";

// Codes are held as "CODE - Label", but users often paste the bare code, which is what the server
// matches on when it saves them.
const codeOf = (tag) =>
  String(tag?.value ?? tag ?? "")
    .split(" - ")[0]
    .trim();

// Remote tagInput: fetches suggestions from the server on user input.
// The element must have a `data-remote-url` attribute with the search endpoint base URL.
// The URL will be called with a `&query=` parameter appended.
// A code the referential turns out not to know is flagged on the spot, because pasting one used to
// look exactly like picking a real one. The flag is only a warning: the tag is still submitted, and
// the server is what actually accepts or rejects it, so flagging a code by mistake costs nothing.
const remoteTagInput = (element) => {
  const initialValue = element.value;
  const initialWhitelist = initialValue ? JSON.parse(initialValue) : [];
  const remoteUrl = element.dataset.remoteUrl;
  const unknownText = getUITranslation("Code invalide");

  // A refused save re-renders the codes that were submitted, so the initial value is not only made
  // of codes that exist: the server tells us which ones it turned down.
  const rejected = new Set(JSON.parse(element.dataset.unknownCodes || "[]"));

  const options = {
    enforceWhitelist: false,
    whitelist: initialWhitelist.filter((entry) => !rejected.has(codeOf(entry))),
    dropdown: {
      maxItems: 20,
      enabled: 0,
      closeOnSelect: false,
      classname: "tags-look",
    },
  };

  if (element.dataset.maxTags) {
    options.maxTags = parseInt(element.dataset.maxTags, 10);
  }

  const tagify = new Tagify(element, options);

  // Both only ever grow: a code the server confirmed once must not become suspect because a later
  // search returned something else.
  const known = new Map(options.whitelist.map((entry) => [codeOf(entry), entry]));
  const answered = new Set([...known.keys(), ...rejected]);

  const fetchCodes = (query, signal) =>
    fetch(`${remoteUrl}&query=${encodeURIComponent(query)}`, { signal })
      .then((res) => res.json())
      .then((results) => {
        results.forEach((entry) => known.set(codeOf(entry), entry));
        tagify.whitelist = [...known.values()];
      });

  const flagUnknownTags = () => {
    tagify.getTagElms().forEach((tagElm) => {
      const code = codeOf(tagElm.__tagifyTagData);
      // A code nobody asked the server about yet is not suspect, it is simply unverified.
      if (!answered.has(code)) return;

      tagElm.dataset.unknownCodeLabel = unknownText;
      tagElm.classList.toggle(UNKNOWN_CODE_CLASS, !known.has(code));
    });
  };

  // Suggestions follow the caret, so only the latest query matters and earlier ones are dropped.
  let suggestController;
  let debounceTimer;

  tagify.on("input", ({ detail }) => {
    const query = detail.value;
    if (query.length < 2) return;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      suggestController?.abort();
      const current = (suggestController = new AbortController());

      tagify.loading(true);

      fetchCodes(query, current.signal)
        .then(() => tagify.loading(false).dropdown.show(query))
        .catch(() => {
          if (suggestController === current) tagify.loading(false);
        })
        .finally(flagUnknownTags);
    }, 300);
  });

  // Unlike a suggestion, a lookup decides whether a tag gets flagged, so it is never cancelled and
  // never asked twice for the same code.
  const lookups = new Map();

  const verifyTag = ({ detail }) => {
    const code = codeOf(detail.data);
    if (known.has(code)) return;

    if (!lookups.has(code)) {
      lookups.set(
        code,
        fetchCodes(code)
          .then(() => answered.add(code))
          .catch(() => {})
      );
    }

    lookups.get(code).then(flagUnknownTags);
  };

  tagify.on("add", verifyTag);
  // Tags can also be rewritten in place, on a double click.
  tagify.on("edit:updated", verifyTag);

  if (rejected.size) flagUnknownTags();

  return tagify;
};

export default tagInput;
export { remoteTagInput };
